/* Still-image fallback motion. Living videos are handled by banner-living.js. */
(() => {
  const instances = new Map();
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  function setup(root) {
    if (root.hasAttribute('data-living-banner')) return;
    if (instances.has(root)) return;
    const scenes = [...root.querySelectorAll('[data-banner-scene]')];
    const controls = root.querySelector('[data-banner-controls]');
    if (scenes.length !== 2 || !controls) return;
    const dots = [...controls.querySelectorAll('[data-scene-select]')];
    const toggle = controls.querySelector('[data-scene-toggle]');
    const caption = controls.querySelector('[data-scene-caption]');
    const labels = dots.map(dot => dot.getAttribute('aria-label').replace(/^Show scene \d: /, ''));
    const interval = Math.max(5, Math.min(15, Number(root.dataset.interval) || 6)) * 1000;
    const fade = Math.max(600, Math.min(2000, Number(root.dataset.fade) || 1400));
    const abort = new AbortController();
    const listen = (target, event, fn) => target.addEventListener(event, fn, {signal: abort.signal});
    const dataSaving = Boolean(navigator.connection?.saveData);
    let paused = root.dataset.autoplay !== 'true' || motionPreference.matches || dataSaving || Boolean(window.Shopify?.designMode);
    let current = 0, timer, transitionTimer, idle, busy = false, destroyed = false;
    let visible = false, focused = false;
    let secondLoad;
    function running() { return !paused && visible && !document.hidden && !focused; }
    function sync() {
      clearTimeout(timer);
      root.classList.toggle('is-running', running());
      root.dataset.motionState = paused ? 'paused' : running() ? 'playing' : 'suspended';
      toggle.setAttribute('aria-label', paused ? 'Play banner motion' : 'Pause banner motion');
      const toggleText = toggle.querySelector('[data-scene-toggle-label]');
      if (toggleText) toggleText.textContent = paused ? 'Play' : 'Pause';
      toggle.querySelector('[data-scene-play]').toggleAttribute('hidden', !paused);
      toggle.querySelector('[data-scene-pause]').toggleAttribute('hidden', paused);
      if (running() && !busy) timer = setTimeout(() => show(1 - current, false), interval);
    }
    function loadSecond() {
      if (secondLoad) return secondLoad;
      secondLoad = new Promise((resolve, reject) => {
        const img = scenes[1].querySelector('img');
        const timeout = setTimeout(() => finish(new Error('Scene load timeout')), 20000);
        function finish(error) {
          clearTimeout(timeout);
          abort.signal.removeEventListener('abort', cancelled);
          img.removeEventListener('load', loaded);
          img.removeEventListener('error', failed);
          error ? reject(error) : resolve();
        }
        async function loaded() {
          try { if (img.decode) await img.decode(); finish(); } catch (error) { finish(error); }
        }
        function failed() { finish(new Error('Scene unavailable')); }
        function cancelled() { finish(new Error('Section removed')); }
        abort.signal.addEventListener('abort', cancelled, {once:true});
        img.addEventListener('load', loaded, {once:true});
        img.addEventListener('error', failed, {once:true});
        scenes[1].querySelectorAll('[data-scene-srcset]').forEach(el => { el.srcset = el.dataset.sceneSrcset; });
        img.src = img.dataset.sceneSrc;
        if (img.complete && img.naturalWidth) loaded();
      });
      return secondLoad;
    }
    function fail() {
      if (destroyed) return;
      paused = true;
      busy = false;
      root.dataset.motionError = 'second-image-unavailable';
      controls.hidden = true;
      sync();
    }
    async function show(next, manual) {
      if (busy || next === current || destroyed) return;
      if (manual) paused = true;
      busy = true;
      sync();
      try { if (next === 1) await loadSecond(); } catch { fail(); return; }
      if (destroyed) return;
      // Do not start an automatic transition after the visitor has paused or left.
      if (!manual && !running()) { busy = false; sync(); return; }
      scenes[next].classList.add('is-entering');
      scenes[next].removeAttribute('aria-hidden');
      scenes[current].setAttribute('aria-hidden','true');
      dots.forEach((dot, index) => {
        if (index === next) dot.setAttribute('aria-current','true');
        else dot.removeAttribute('aria-current');
      });
      caption.textContent = labels[next];
      root.dataset.activeScene = String(next + 1);
      // The outgoing image stays fully opaque underneath: no cream/black flash.
      transitionTimer = setTimeout(() => {
        scenes[current].classList.remove('is-active');
        scenes[next].classList.add('is-active');
        scenes[next].classList.remove('is-entering');
        current = next;
        busy = false;
        sync();
      }, motionPreference.matches ? 0 : fade);
    }
    listen(toggle, 'click', () => {
      paused = !paused;
      // An explicit Play command also works while focus remains on this button.
      if (!paused) focused = false;
      sync();
    });
    dots.forEach((dot,index) => listen(dot,'click', () => {
      paused = true;
      sync();
      show(index, true);
    }));
    // Looking at a photo with the pointer over it must not silently stop it.
    listen(controls, 'focusin', () => { focused = true; sync(); });
    listen(controls, 'focusout', event => { if (!controls.contains(event.relatedTarget)) { focused = false; sync(); } });
    listen(document, 'visibilitychange', sync);
    listen(motionPreference, 'change', () => { if (motionPreference.matches) paused = true; sync(); });
    const observer = new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      sync();
    }, {threshold:0.12});
    observer.observe(root);
    controls.hidden = false;
    root.dataset.activeScene = '1';
    sync();
    // Fetch scene two only after the first image is decoded, with low priority.
    const first = scenes[0].querySelector('img');
    const warm = () => {
      if (destroyed || dataSaving || paused) return;
      if ('requestIdleCallback' in window) idle = requestIdleCallback(() => loadSecond().catch(fail), {timeout:2000});
      else idle = setTimeout(() => loadSecond().catch(fail), 600);
    };
    if (first?.decode) first.decode().then(warm).catch(() => {});
    else if (first?.complete) warm();
    else if (first) listen(first,'load',warm);
    instances.set(root, () => {
      destroyed = true;
      clearTimeout(timer);
      clearTimeout(transitionTimer);
      if ('cancelIdleCallback' in window) cancelIdleCallback(idle);
      else clearTimeout(idle);
      observer.disconnect();
      abort.abort();
      instances.delete(root);
    });
  }
  const init = scope => scope.querySelectorAll('[data-banner-motion]').forEach(setup);
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((destroy, root) => { if (event.target.contains(root)) destroy(); });
  });
})();
